import React from "react";
import styled, { keyframes } from "styled-components";
import tw from "twin.macro";
import {
  Container as ContainerBase,
  ContentWithPaddingXl,
} from "components/misc/Layouts";
import nccBuildingImg from "../../images/GroupCompany/ncc-building-motoasakusa.jpeg";
import BgTitle from "components/title/BgTitle";
import { useTranslation } from "react-i18next";
import HistoryOverviewMobileView from "./HistoryOverviewMobileView";

const Container = styled(ContainerBase)`
  ${tw`relative`}
`;

const TwoColumn = styled.div`
  ${tw`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-6 md:py-8`} // Reduced padding
  @media (max-width: 768px) {
    ${tw`flex-col`}
  }
`;

const Column = styled.div`
  ${tw`w-full md:w-1/2 max-w-none mx-auto md:mx-0`}
  max-height: 500px;
`;

const LeftColumn = styled(Column)`
  ${tw`flex-shrink-0`}
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem; // Reduced padding
`;

const RightColumnContainer = styled.div`
  perspective: 1000px; /* Adds a perspective for 3D effect */
  max-height: 500px;
`;

const RightColumn = styled(Column)`
  ${tw`flex-shrink-0`}
  display: flex;
  align-items: center;
  transform: rotateY(-10deg) translateZ(150px); /* Adjust for a 3D z-axis effect */
  width: 100%;
`;

const Image = styled.img`
  ${tw`w-full rounded-lg`}
  max-width: 500px;
  height: auto;
  max-height: 500px;
`;

const ScrollContainer = styled.div`
  ${tw`relative overflow-hidden p-3`} // Reduced padding
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fafafa;
  border-radius: 12px;
  max-height: 500px;
  width: 100%;
`;

const MarqueeCard = styled.div`
  ${tw`w-full p-3 mb-3 rounded-lg`} // Reduced padding and margin
  background-color: #fafafa;
  border: 1px dashed rgb(180, 186, 197);
  font-size: 0.8rem;
  color: #333;
  text-align: center;
  width: 100%;
`;

const TopOverlay = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 20%;
  background: linear-gradient(
    to bottom,
    rgba(250, 250, 250, 1),
    rgba(250, 250, 250, 0)
  );
  pointer-events: none;
  z-index: 1;
`;

const BottomOverlay = styled.div`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 20%;
  background: linear-gradient(
    to top,
    rgba(250, 250, 250, 1),
    rgba(250, 250, 250, 0)
  );
  pointer-events: none;
  z-index: 1;
`;

// Adjusted continuous scroll animation
const marqueeScroll = keyframes`
  0% { transform: translateY(0); }
  100% { transform: translateY(-100%); }
`;

const MarqueeList = styled.div`
  display: flex;
  flex-direction: column;
  animation: ${marqueeScroll} 50s linear infinite;
  &:after {
    content: "";
    display: block;
    margin-top: -100%;
  }
`;

export default ({ isXs = false }) => {
  const { t } = useTranslation();
  if (isXs) return <HistoryOverviewMobileView />; // This is a mobile view
  const companyHistory = t("company_history.events", { returnObjects: true });

  return (
    <Container>
      <ContentWithPaddingXl>
        <BgTitle text={t("company_history.title")} />
      </ContentWithPaddingXl>
      <TwoColumn>
        <LeftColumn>
          <Image src={nccBuildingImg} alt="NCC Cloud Office" />
        </LeftColumn>

        <RightColumnContainer>
          <RightColumn>
            <ScrollContainer>
              <TopOverlay />
              <MarqueeList>
                {companyHistory.concat(companyHistory).map((item, idx) => (
                  <MarqueeCard key={idx}>
                    <strong>{item.year}</strong> - {item.event}
                  </MarqueeCard>
                ))}
              </MarqueeList>
              <BottomOverlay />
            </ScrollContainer>
          </RightColumn>
        </RightColumnContainer>
      </TwoColumn>
    </Container>
  );
};
